/**
 * Camada 1 · rail lateral colapsavel.
 *
 * O ESTADO COLAPSADO CONTA COMO DISPOSITIVO. Componente que nasce recolhido precisa ser
 * exercitado nos dois estados, porque metade da interface costuma ficar presa a um deles,
 * e o defeito so aparece para quem abriu a tela do jeito que ninguem testou.
 *
 * No celular o rail vira gaveta sobreposta em vez de coluna, porque uma coluna fixa de
 * 240px em 390px de largura nao deixa conteudo nenhum.
 *
 * O QUE ENTROU EM 29/08/2026, na migracao do prontidao, e por que cada coisa:
 *   · ICONE DE VERDADE (V03): o nome escrito no ProductConfig vira desenho pelo mapa de
 *     @complexity/ui. Antes era texto, e o manifesto usava emoji, que muda de desenho por
 *     sistema operacional. Num produto de aviacao o icone de aeronave precisa ser o mesmo
 *     no Mac, no Windows e no Android.
 *   · DESTAQUE (V02): um item, no maximo, e o loader recusa dois. E o gesto que a operacao
 *     faz todo dia, e o legado o realca por isso.
 *   · DIVISORIA POR SECAO (V04): separa a operacao do que e o proprio sistema. Sem ela,
 *     "Chaves de acesso" parece um modulo da operacao e o piloto procura voo dentro dele.
 *   · MARCA CLICAVEL (V26): so quando o produto declara `rotaDaMarca`. Marca que parece
 *     botao e nao navega e affordance mentirosa, e custa mais que marca que nao parece botao.
 */

import { AssinaturaDaPlataforma } from '@complexity/brand';
import type { ItemMenu } from '@complexity/product-kit';
// O rotulo de cada secao mora no contrato, e nao aqui: e o mesmo texto que a gaveta do
// celular desenha, e dois lugares divergiriam no dia em que um deles mudasse. ADR 0051.
import { ROTULO_SECAO } from '@complexity/product-kit';
import { t } from '@complexity/i18n';
import { Simbolo } from '@complexity/brand';
import { useEffect, useState } from 'react';
import {
  icone as resolverIcone,
  IconePainelLateral,
  IconeAjustes,
  IconeSair,
} from '@complexity/ui';

/**
 * Uma linha de dado da pessoa logada, no painel que a conta abre com `sairNaConta` ligado. O
 * rotulo passa pelo dicionario; o valor nao, porque e dado da pessoa. VALOR NULO E AUSENCIA
 * DITA: o painel escreve "Não cadastrado", e nunca um valor provavel.
 */
export interface DadoDaConta {
  readonly rotulo: string;
  readonly valor: string | null;
}

export interface RailProps {
  readonly nomeProduto: string;
  readonly itens: readonly ItemMenu[];
  readonly rotaAtiva: string;
  readonly aoNavegar: (rota: string) => void;
  readonly colapsado: boolean;
  readonly aoAlternar: () => void;
  /**
   * Se o controle de colapsar TEM efeito nesta largura.
   *
   * DEFEITO MEDIDO EM 30/08/2026, 01:34: em 768px o botao era desenhado e o clique NAO
   * mudava nada. O `AppShell` forca `colapsadoNaTela = true` no tablet, por decisao da linha
   * V23 da paridade, entao a preferencia mudava e o desenho ficava igual.
   *
   * Botao que nao faz nada e affordance mentirosa, que e o mesmo defeito que este
   * repositorio ja nomeou noutro lugar: "botao que nao navega e affordance mentirosa". Quem
   * clica conclui que a tela travou, e nao que aquilo nao se aplica ali.
   */
  readonly alternarTemEfeito?: boolean;
  /** No celular a gaveta comeca fechada e so aparece quando pedida. */
  readonly abertoNoCelular: boolean;
  readonly aoFecharNoCelular: () => void;
  /**
   * Para onde o clique na marca leva. Ausente, a marca NAO e clicavel, e continua sendo
   * apenas identidade. Ver `ProductConfig.rotaDaMarca`.
   */
  readonly rotaDaMarca?: string;
  /**
   * O simbolo do cabecalho com 28px em vez de 36px. Ausente, 36px, que e o desenho do retrato
   * congelado do VOE. Ver `CabecalhoDoMenu.simboloCompacto` em packages/product-kit.
   */
  readonly simboloCompacto?: boolean;
  /**
   * O cabecalho INTEIRO vira o controle do menu. Ausente, a marca e o botao de recolher
   * continuam separados, como no retrato congelado do VOE. Ligado, `rotaDaMarca` nao se
   * aplica. Recolhido, fica so o icone de expandir, e nenhum icone do menu muda de lugar entre
   * os dois estados. Ver `CabecalhoDoMenu.recolheAoClicar` em packages/product-kit.
   */
  readonly cabecalhoRecolhe?: boolean;
  /**
   * A assinatura da plataforma no pe do menu. Ausente, `true`, como no retrato congelado do
   * VOE. Ver `ProductConfig.assinaturaNoMenu` em packages/product-kit.
   */
  readonly assinaturaNoMenu?: boolean;
  /**
   * O Sair mora dentro do painel da conta, e sai do pe do menu. Ausente, `false`, como no
   * retrato congelado do VOE. Ver `ProductConfig.sairNaConta` em packages/product-kit.
   */
  readonly sairNaConta?: boolean;
  /** As linhas do painel da conta, na ordem em que aparecem. So valem com `sairNaConta`. */
  readonly dadosDaConta?: readonly DadoDaConta[];
  /**
   * O RODAPE DO MENU, movido da topbar em 30/08/2026 a pedido dele: "TB CONFIGURACOES E
   * USUARIO VAO PRA BAIXO NO MENU". Ate entao os dois moravam na direita da topbar, que e
   * onde a regra dura do CLAUDE geral manda o idioma e o tema ficarem. Os controles de
   * idioma e tema CONTINUAM la em cima; o que desce e a conta da pessoa.
   */
  readonly nomeUsuario: string;
  readonly opcoesUsuario?: readonly { readonly label: string; readonly aoClicar: () => void }[];
  readonly aoSair: () => void;
}

/**
 * Desenha o icone do item.
 *
 * DOIS CAMINHOS, E O SEGUNDO E O QUE IMPEDE UMA REGRESSAO. Nome que o mapa conhece vira SVG.
 * Nome que ele nao conhece e desenhado COMO TEXTO, que era o comportamento desta chassi antes
 * de 29/08/2026.
 *
 * POR QUE O TEXTO E NAO UM PONTO GENERICO, medido no mesmo dia: os outros produtos da
 * plataforma escrevem emoji no campo `icone`, e o mapa nao os conhece. Trocar o desconhecido
 * por um ponto teria apagado o icone de TODOS eles em silencio, num commit que so falava do
 * prontidao. Ampliar o chassi nunca pode piorar quem ja a usava.
 *
 * `h-5 w-5` nos dois caminhos: e o que mantem o ritmo da coluna igual, com SVG ou com emoji.
 */
function IconeDoItem({ nome }: { readonly nome: string }) {
  const Desenho = resolverIcone(nome);
  if (!Desenho) {
    // NOME DESCONHECIDO VIRA UM PONTO, e NUNCA o texto cru. Ate 30/08/2026 este ramo
    // desenhava `{nome}`, e era por ele que o emoji chegava a tela: o `product.config` de um
    // produto nomeava '🏢', o mapa nao resolvia, e o rail pintava o emoji. O fundador
    // fechou a regra naquele dia, e o mecanismo mora aqui e nao numa instrucao escrita.
    // O ponto ocupa a mesma caixa de 20px do desenho, entao a lista nao desalinha.
    return (
      <span aria-hidden="true" className="flex h-5 w-5 shrink-0 items-center justify-center">
        <span className="h-1.5 w-1.5 rounded-cx-full bg-texto-fraco" />
      </span>
    );
  }
  return <Desenho aria-hidden="true" className="h-5 w-5 shrink-0" />;
}

/** Tratamentos que nao sao nome, e por isso nunca entram nas INICIAIS. */
const TRATAMENTOS = new Set(['dr', 'dra', 'sr', 'sra', 'prof', 'profa']);

/**
 * O nome como o pe do menu o mostra com `sairNaConta`: a forma de tratamento, o primeiro nome
 * e o ultimo sobrenome, e as iniciais SO dos dois ultimos.
 *
 * ---------------------------------------------------------------------------
 * SAO DOIS PEDIDOS DELE, e o segundo mudou metade do primeiro
 * ---------------------------------------------------------------------------
 * 12/09/2026: *"na imagem vai ficar AC dentro do circulo e fora Antonio Carlos (se tivesse
 * tres nomes com sobrenome ou mais pegar as iniciais do nome e ultimo sobrenome)"*. Dali
 * nasceu a regra de encurtar, e junto com ela o descarte do tratamento.
 *
 * 14/09/2026, apontando o rodape: *"aqui tb tem que ter a forma de tratamento"*. Em enquete,
 * ele escolheu a opcao que mantem as INICIAIS como estavam: o circulo continua "MB", e o
 * texto ao lado passa a ser "Dr. Marcelo Barros".
 *
 * ENTAO O TRATAMENTO MUDOU DE PAPEL AQUI, e nao sumiu de novo: ele deixa de ser descartado e
 * passa a ser SEPARADO. Sai das iniciais, onde "DM" nao identifica ninguem, e volta ao texto,
 * onde e como a pessoa e chamada.
 *
 * Funcao pura, sem estado, para o calculo ser o mesmo em toda renderizacao.
 */
function nomeParaExibir(nome: string): { readonly curto: string; readonly iniciais: string } {
  const todas = nome.trim().split(/\s+/).filter((p) => p !== '');
  const ehTratamento = (p: string) => TRATAMENTOS.has(p.replace(/\.$/, '').toLowerCase());

  /*
   * SO O PRIMEIRO PEDACO PODE SER TRATAMENTO, e a diferenca importa: o filtro antigo varria
   * o nome inteiro, entao um sobrenome que por acaso casasse com a lista (um "Sr" no meio)
   * sumiria do texto. Prefixo e posicao, e nao palavra solta.
   */
  const prefixo = todas[0] && ehTratamento(todas[0]) ? todas[0] : null;
  const partes = prefixo ? todas.slice(1) : todas;

  // SO O TRATAMENTO, sem nome nenhum, nao vira "Dr." sozinho na tela: devolve o que veio.
  if (partes.length === 0) return { curto: nome.trim(), iniciais: '?' };

  const primeiro = partes[0]!;
  const ultimo = partes.length > 1 ? partes[partes.length - 1]! : null;
  const curto = ultimo ? `${primeiro} ${ultimo}` : primeiro;
  return {
    // O prefixo volta AQUI, e so aqui. As iniciais abaixo continuam saindo de `partes`, que
    // ja esta sem ele: e isso que mantem "MB" no circulo, como ele decidiu.
    curto: prefixo ? `${prefixo} ${curto}` : curto,
    iniciais: `${primeiro[0] ?? ''}${ultimo?.[0] ?? ''}`.toUpperCase() || '?',
  };
}

export function Rail({
  nomeProduto,
  itens,
  rotaAtiva,
  aoNavegar,
  colapsado,
  aoAlternar,
  alternarTemEfeito = true,
  abertoNoCelular,
  aoFecharNoCelular,
  rotaDaMarca,
  simboloCompacto = false,
  cabecalhoRecolhe = false,
  assinaturaNoMenu = true,
  sairNaConta = false,
  dadosDaConta = [],
  nomeUsuario,
  opcoesUsuario = [],
  aoSair,
}: RailProps) {
  // Qual dos dois menus do rodape esta aberto, ou nenhum. UM estado com tres valores, e nao
  // dois booleanos: com dois, existe o estado invalido "os dois abertos ao mesmo tempo", e
  // ele um dia acontece. Aqui abrir um FECHA o outro por construcao.
  const [menuAberto, setMenuAberto] = useState<'nenhum' | 'ajustes' | 'conta'>('nenhum');

  /**
   * O MENU DO RODAPE FECHA AO CLICAR FORA DELE, e com Esc.
   *
   * Pedido do fundador em 14/09/2026, com o painel da conta aberto na tela: *"tb com essa
   * janela aberta fechar clicando em qualquer outro lugar fora dela"*.
   *
   * ---------------------------------------------------------------------------
   * O QUE ELE CONSERTA, e e o comportamento que toda plataforma grande tem
   * ---------------------------------------------------------------------------
   * Ate aqui o painel so fechava pelo MESMO botao que o abriu. Quem abre por engano
   * precisa achar aquele botao de novo, e enquanto isso o painel cobre o menu embaixo
   * dele. Num painel que mostra CPF e CRM, ficar aberto tem um custo a mais: ele continua
   * na tela enquanto a pessoa faz outra coisa.
   *
   * `pointerdown` E NAO `click`: o clique so nasce no soltar, entao com `click` o painel
   * fecharia DEPOIS de o gesto ja ter alcancado o que estava embaixo. E `pointer*` cobre
   * dedo e cursor com um evento so, que e a regra desta casa para gesto.
   *
   * `closest` COM O ATRIBUTO, e nao uma `ref` por menu: sao DOIS menus no rodape, e com
   * refs seriam duas, mais a comparacao de cada uma. O atributo marca a fronteira no
   * proprio markup, e o terceiro menu que nascer amanha ja entra coberto.
   *
   * O EFEITO SO EXISTE COM MENU ABERTO, e a dependencia garante isso: um ouvinte de
   * documento vivo o tempo todo roda em todo clique da aplicacao para nao fazer nada.
   */
  useEffect(() => {
    if (menuAberto === 'nenhum') return;
    const fecharSeForFora = (evento: PointerEvent) => {
      const alvo = evento.target as HTMLElement | null;
      // Dentro do proprio menu (ou do botao que o abre) nao fecha: ali o clique tem dono.
      if (alvo?.closest?.('[data-cx-menu-rodape]')) return;
      setMenuAberto('nenhum');
    };
    const fecharNoEsc = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setMenuAberto('nenhum');
    };
    document.addEventListener('pointerdown', fecharSeForFora);
    document.addEventListener('keydown', fecharNoEsc);
    return () => {
      document.removeEventListener('pointerdown', fecharSeForFora);
      document.removeEventListener('keydown', fecharNoEsc);
    };
  }, [menuAberto]);

  // A INICIAL, e nao um retrato: nao existe upload de foto neste produto, e uma letra e
  // legivel nos 32px do avatar, onde um icone generico de pessoa nao distingue ninguem.
  //
  // COM `sairNaConta` SAO DUAS INICIAIS E O NOME CURTO (ver `nomeParaExibir`). Sem a opcao, a
  // letra unica de sempre, que e o retrato congelado do VOE.
  const exibicao = nomeParaExibir(nomeUsuario);
  const inicial = sairNaConta ? exibicao.iniciais : (nomeUsuario.trim()[0] ?? '?').toUpperCase();

  // Quantas secoes DISTINTAS este produto declara. E o que decide se os cabecalhos de grupo
  // do ADR 0051 aparecem: com uma secao so, "Operação" no topo nao separa nada de nada, e
  // cabecalho que nao agrupa e apenas uma linha a mais para ler.
  //
  // Contado UMA VEZ, e nao dentro do map: la dentro, um produto com 14 destinos refaria a
  // mesma conta 14 vezes para chegar sempre no mesmo resultado.
  const temMaisDeUmaSecao = new Set(itens.map((i) => i.secao ?? 'operacao')).size > 1;
  // O bloco da marca e o mesmo desenho nos dois casos; o que muda e ser <button> ou <div>.
  // Extrair o conteudo evita escrever o mesmo markup duas vezes, e markup duplicado e
  // markup que envelhece de um lado so.
  const conteudoDaMarca = (
    <>
      {/* O SIMBOLO DA MARCA, e nao as iniciais do produto. Pedido dele em 29/08/2026:
          duas letras num quadrado azul liam como avatar de usuario, e nao como a marca da
          casa. E o mesmo simbolo do login e do site institucional, gerado do mesmo .svg,
          entao os tres nao podem divergir.

          `text-marca` no lugar de `bg-marca`: o desenho usa `currentColor`, entao a cor
          passa a ser a do texto e ele acompanha tema claro e escuro sem duplicar arquivo. */}
      <span
        aria-hidden="true"
        // 28px SO QUANDO O PRODUTO PEDE, e a classe dos outros sai identica a do retrato
        // congelado do VOE. Pedido do fundador em 12/09/2026 no Medicina: "diminuir a logo".
        className={[
          'flex',
          simboloCompacto ? 'h-7 w-7' : 'h-9 w-9',
          'shrink-0 items-center justify-center text-marca',
        ].join(' ')}
        // Marcador para a suite, e so no cabecalho novo: o DOM dos outros produtos nao muda.
        data-simbolo-do-menu={cabecalhoRecolhe ? '' : undefined}
      >
        <Simbolo className="h-full w-full" />
      </span>
      {/* O NOME ACOMPANHA SEMPRE, e ele sai da TELA e nao do LEITOR.
          CORRIGIDO EM 30/08/2026, e o caso generico do rail colapsado e que pegou. O
          comentario que estava aqui dizia que o nome continuava na arvore "pelo aria-label
          do <nav>", e isso e verdade para a REGIAO e falso para o BOTAO: quando o produto
          declara `rotaDaMarca`, este bloco vira um <button> clicavel que navega para outra
          tela, e colapsado ele nao tinha aria-label, nao tinha title e nao tinha texto, com
          o simbolo em `aria-hidden`. Era um botao mudo, e o pior dos tres achados do dia,
          porque os outros dois ao menos tinham `aria-label`. */}
      {colapsado ? (
        <span className="cx-so-leitor" data-nome-do-menu={cabecalhoRecolhe ? '' : undefined}>
          {nomeProduto}
        </span>
      ) : (
        <span
          className="truncate text-cx-sm font-semibold text-texto"
          data-nome-do-menu={cabecalhoRecolhe ? '' : undefined}
        >
          {nomeProduto}
        </span>
      )}
    </>
  );

  return (
    <>
      {/* Cortina do celular. So existe com a gaveta aberta, e fecha ao toque fora dela.
          `z-painel` e nao `z-rail`: ver o comentario do <nav> logo abaixo. */}
      {abertoNoCelular && (
        <button
          type="button"
          aria-label={t('Fechar menu')}
          onClick={aoFecharNoCelular}
          className="fixed inset-0 z-painel bg-superficie-inversa/50 md:hidden"
        />
      )}

      <nav
        aria-label={t('Menu do produto')}
        className={[
          'flex h-full flex-col border-r border-borda bg-superficie-elevada',
          'transition-[width] duration-200',
          // No celular e gaveta fixa que desliza; do tablet para cima e coluna no fluxo.
          'fixed inset-y-0 left-0 md:static',

          /**
           * DEFEITO MEDIDO EM 29/08/2026, NAO REVERTER SEM LER ISTO.
           *
           * O rail tinha `z-rail` (20) e a topbar tem `z-topbar` (30), entao no celular a
           * gaveta sobreposta nascia DEBAIXO da topbar: os primeiros ~48px dela ficavam
           * cobertos, e o que mora ali e a MARCA e o comeco do menu.
           *
           * O defeito era antigo e invisivel: enquanto a marca era um <div>, ninguem tentava
           * toca-la. Ele so apareceu quando ela virou botao (V26) e o teste da suite tentou
           * clicar, recebendo "o botao Avisos intercepta os eventos de ponteiro". Um caso de
           * teste pegou o que trinta e tantas execucoes anteriores nao pegaram, porque
           * nenhuma delas exercitava aquele ponto da tela.
           *
           * `z-painel` (40) SO NO CELULAR, e `md:z-rail` de volta ao normal do tablet para
           * cima: la o rail e coluna no fluxo e nao disputa camada com ninguem. Gaveta
           * sobreposta que nao fica por cima nao e gaveta, e uma tira escondida.
           */
          'z-painel md:z-rail',

          abertoNoCelular ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
          colapsado ? 'w-16' : 'w-60',
        ].join(' ')}
      >
        {/* A LINHA DO TOPO: a marca a esquerda, o controle de recolher a direita.
            
            Ate 30/08/2026 o controle morava no RODAPE, com o rotulo "Recolher menu" ao lado
            de um `«` tipografico. Ele subiu por pedido dele naquele dia, com a captura da
            barra do Gemini, e PERDEU O TEXTO na mesma decisao: "NAO VAI TER TEXTO TB".
            O rodape passou a ser o lugar da conta, e dois controles disputando o mesmo
            rodape fariam o menos usado esconder o mais usado.

            COLAPSADO A LINHA VIRA COLUNA. Em 64px de largura o simbolo e o botao nao cabem
            lado a lado sem um dos dois cair abaixo do alvo minimo de toque, e alvo de dedo
            nao se negocia por causa de layout. */}
        <div
          className={[
            'flex shrink-0 border-b border-borda',
            // COM `cabecalhoRecolhe` A LINHA TEM A MESMA ALTURA NOS DOIS ESTADOS: recolhida fica so
            // o icone de expandir, e os icones do menu abaixo nao sobem nem descem. Pedido do
            // fundador em 12/09/2026: "nao ficar deslocando a posicao dos icones fechado o
            // expandido". Sem a opcao, o desenho e o de sempre.
            cabecalhoRecolhe
              ? colapsado
                ? 'items-center justify-center px-cx-2 py-cx-3'
                : 'items-center gap-cx-2 p-cx-3'
              : colapsado
                ? 'flex-col items-center gap-cx-1 p-cx-2'
                : 'items-center gap-cx-2 p-cx-3',
          ].join(' ')}
        >
          {/* A MARCA. Vira botao SO quando o produto declara para onde ela leva, OU quando ele
              pede que o cabecalho inteiro controle o menu (`cabecalhoRecolhe`). */}
          {cabecalhoRecolhe ? (
            alternarTemEfeito ? (
              <button
                type="button"
                data-cabecalho-do-menu=""
                // NA GAVETA DO CELULAR, RECOLHER E FECHAR A GAVETA: ela so abre abaixo de 768px,
                // entao `abertoNoCelular` verdadeiro ja diz em que largura a pessoa esta.
                onClick={abertoNoCelular ? aoFecharNoCelular : aoAlternar}
                aria-expanded={!colapsado}
                title={colapsado ? t('Expandir menu') : t('Recolher menu')}
                className={[
                  'flex min-h-toque w-full items-center rounded-cx-md text-left hover:bg-superficie-afundada',
                  colapsado ? 'justify-center' : 'min-w-0 gap-cx-2 px-cx-1',
                ].join(' ')}
              >
                {/* RECOLHIDO, O SIMBOLO SAI e fica so o icone de expandir. Pedido do fundador em
                    12/09/2026: "com o menu retraido o C sai, fica apenas o icone de expandir".
                    O nome do produto continua para o leitor de tela, dentro do nome do botao. */}
                {colapsado ? (
                  <span className="cx-so-leitor" data-nome-do-menu="">
                    {nomeProduto}
                  </span>
                ) : (
                  conteudoDaMarca
                )}
                {/* O NOME ACESSIVEL DIZ O QUE O CLIQUE FAZ, e o nome do produto continua nele,
                    porque e o texto que a pessoa ve. A frase muda por largura pelo CSS:
                    conteudo com `display: none` nao entra no nome acessivel, entao cada largura
                    anuncia so a sua. */}
                <span className="cx-so-leitor md:hidden">, {t('Fechar menu')}</span>
                <span className="cx-so-leitor hidden md:inline">
                  , {colapsado ? t('Expandir menu') : t('Recolher menu')}
                </span>
                {/* O MESMO desenho do botao separado que ele substitui, para quem ja conhece o
                    gesto. No celular nao aparece, como o botao antigo nao aparecia. */}
                <IconePainelLateral
                  aria-hidden="true"
                  className={[
                    'hidden h-5 w-5 shrink-0 text-texto-suave md:block',
                    colapsado ? '' : 'ml-auto',
                  ].join(' ')}
                />
              </button>
            ) : (
              // NO TABLET O MENU E SEMPRE TIRA (V23), entao nao ha o que recolher: o cabecalho
              // volta a ser so identidade, e nao um botao que nao faz nada.
              <div
                data-cabecalho-do-menu=""
                className={[
                  'flex min-h-toque items-center',
                  colapsado ? 'w-full justify-center' : 'min-w-0 flex-1 gap-cx-2 px-cx-1',
                ].join(' ')}
              >
                {conteudoDaMarca}
              </div>
            )
          ) : rotaDaMarca ? (
            <button
              type="button"
              onClick={() => aoNavegar(rotaDaMarca)}
              // O alvo minimo vale aqui como em qualquer outro botao: a marca no topo do menu
              // e o caminho para a tela "Sobre" no legado, e no telefone ela precisa ser
              // acertavel com o polegar.
              className={[
                'flex min-h-toque items-center rounded-cx-md text-left hover:bg-superficie-afundada',
                colapsado ? 'w-full justify-center' : 'min-w-0 flex-1 gap-cx-2 px-cx-1',
              ].join(' ')}
              aria-current={rotaDaMarca === rotaAtiva ? 'page' : undefined}
            >
              {conteudoDaMarca}
            </button>
          ) : (
            <div
              className={[
                'flex min-h-toque items-center',
                colapsado ? 'w-full justify-center' : 'min-w-0 flex-1 gap-cx-2 px-cx-1',
              ].join(' ')}
            >
              {conteudoDaMarca}
            </div>
          )}

          {/* Colapsar e recurso de tela larga: no celular a gaveta ja abre e fecha inteira,
              e um segundo controle no mesmo lugar so confundiria. E no TABLET ele nao
              aparece porque nao teria efeito: ver `alternarTemEfeito` no contrato acima.
              COM `cabecalhoRecolhe` ELE NAO EXISTE: o cabecalho inteiro faz o papel dele, e dois
              controles para o mesmo gesto no mesmo lugar so dividiriam o alvo. */}
          {!cabecalhoRecolhe && alternarTemEfeito && (
            <button
              type="button"
              onClick={aoAlternar}
              aria-expanded={!colapsado}
              /* O NOME ACESSIVEL PRECISA EXISTIR NOS DOIS ESTADOS, e ate 30/08/2026 ele so
                 existia em um. Colapsado, o botao tinha como conteudo apenas o caractere `»`,
                 entao um leitor de tela anunciava "sinal de maior maior" e mais nada: a
                 pessoa nao tinha como saber que aquilo abre o menu.
                 Agora nao ha texto em estado nenhum, por decisao dele, entao o atributo
                 deixou de ser reforco e passou a ser o UNICO nome: sem ele o botao seria
                 anunciado como "botao" e nada mais. */
              aria-label={colapsado ? t('Expandir menu') : t('Recolher menu')}
              title={colapsado ? t('Expandir menu') : t('Recolher menu')}
              className="hidden min-h-toque min-w-toque shrink-0 items-center justify-center rounded-cx-md text-texto-suave hover:bg-superficie-afundada hover:text-texto md:flex"
            >
              {/* O MESMO desenho nos dois estados, de proposito: ver o comentario do
                  `IconePainelLateral`. Quem diz o estado e o `aria-label` acima. */}
              <IconePainelLateral className="h-5 w-5" />
            </button>
          )}
        </div>

        <ul className="flex-1 overflow-y-auto p-cx-2">
          {/* Contado UMA VEZ, e nao dentro do map: dentro, a mesma conta rodaria a cada item,
              e um produto com 14 destinos a faria 14 vezes para chegar sempre no mesmo
              resultado. `?? 'operacao'` repete o padrao do tipo, porque a secao e opcional. */}
          {itens.map((item, indice) => {
            const ativo = item.rota === rotaAtiva;

            // A DIVISORIA E DESENHADA NA TROCA DE SECAO, e nao por um campo separado de
            // "desenhar divisoria aqui": duas fontes para a mesma decisao divergem, e a que
            // fica errada e a que ninguem olha. Ausente vale `operacao`, que e onde a
            // esmagadora maioria dos itens mora.
            const secaoDeste = item.secao ?? 'operacao';
            const secaoDoAnterior = indice > 0 ? (itens[indice - 1]!.secao ?? 'operacao') : secaoDeste;
            const trocouDeSecao = secaoDeste !== secaoDoAnterior;

            // O CABECALHO DE GRUPO, do ADR 0051. Ele aparece na troca de secao E no primeiro
            // item, para a primeira secao tambem ter nome: um grupo nomeado seguido de um
            // grupo anonimo le como se o segundo fosse o unico agrupado.
            //
            // MAS SO QUANDO HA MAIS DE UMA SECAO, e esta condicao e o que impede a regra de
            // virar ruido: num produto com uma secao so, "Operação" no topo nao separa nada
            // de nada, e cabecalho que nao agrupa e apenas uma linha a mais para ler.
            const ehPrimeiro = indice === 0;

            // O CABECALHO NAO REPETE UMA SECAO JA MOSTRADA, e este `if` corrige um defeito
            // que ELE VIU NA CAPTURA em 06/09/2026: o menu do admin desenhava "OPERAÇÃO"
            // DUAS VEZES.
            //
            // A CAUSA: os itens do produto vao `operacao` e depois `sistema`, e a AJUDA
            // entra pelo Chassi SEM secao declarada, entao ela cai de volta em `operacao`
            // no fim da lista. Com so a divisoria isso nao aparecia; o cabecalho que nasceu
            // hoje expos.
            //
            // A DIVISORIA CONTINUA, porque ela diz a coisa certa: aquilo E outro bloco. O
            // que nao se repete e o NOME, porque um titulo que volta faz a pessoa achar que
            // rolou para cima sem querer.
            const jaMostrada = itens
              .slice(0, indice)
              .some((anterior) => (anterior.secao ?? 'operacao') === secaoDeste);
            const mostraCabecalho =
              temMaisDeUmaSecao && (trocouDeSecao || ehPrimeiro) && !jaMostrada;

            return (
              <li key={item.rota}>
                {trocouDeSecao && (
                  // `role="separator"` e nao um <hr> solto: a divisoria e semantica aqui,
                  // ela diz que o que vem abaixo e outro assunto, e quem usa leitor de tela
                  // precisa dessa informacao tanto quanto quem enxerga a linha.
                  <hr role="separator" className="my-cx-2 border-borda" />
                )}
                {mostraCabecalho && (
                  // COLAPSADO ELE SOME DA TELA E FICA PARA O LEITOR, e nao o contrario: no
                  // rail de 64px nao ha largura para a palavra, e escrever mesmo assim
                  // truncaria o texto num ponto qualquer. `cx-so-leitor` mantem a regiao
                  // nomeada para quem navega por leitor, que e quem mais precisa dela.
                  //
                  // POR QUE ISSO IMPORTA: com o rail colapsado, o cabecalho nao pode ser a
                  // UNICA forma de orientar, e nao e: cada item continua com `title` e com o
                  // rotulo falado, que e o que sustenta aquele estado.
                  <p
                    className={
                      colapsado && !cabecalhoRecolhe
                        ? 'cx-so-leitor'
                        : 'px-cx-3 pb-cx-1 pt-cx-2 text-cx-xs font-semibold uppercase tracking-wide text-texto-suave'
                    }
                  >
                    {colapsado && cabecalhoRecolhe ? (
                      // RECOLHIDO, A LINHA DO GRUPO GUARDA A ALTURA e perde a palavra na tela: sem
                      // ela, os icones abaixo subiriam ao recolher. O espaco rigido segura a
                      // linha, e a palavra continua para o leitor de tela.
                      <>
                        <span className="cx-so-leitor">{t(ROTULO_SECAO[secaoDeste])}</span>
                        <span aria-hidden="true">{' '}</span>
                      </>
                    ) : (
                      t(ROTULO_SECAO[secaoDeste])
                    )}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => aoNavegar(item.rota)}
                  aria-current={ativo ? 'page' : undefined}
                  // Atributo de dado, e nao so classe: e por ele que a suite mede o destaque
                  // sem depender do nome de uma classe de estilo, que muda quando alguem
                  // troca a paleta e derrubaria o teste por um motivo que nao e o testado.
                  data-destaque={item.destaque ? 'true' : undefined}
                  // O rotulo textual acompanha SEMPRE, mesmo colapsado, escondido da
                  // tela e nao do leitor: icone sozinho e adivinhacao.
                  title={colapsado ? item.label : undefined}
                  className={[
                    'flex w-full min-h-toque items-center gap-cx-3 rounded-cx-md px-cx-3 text-left text-cx-sm',
                    // O DESTAQUE VEM DO FUNDO, e nao de um icone mais pesado que os vizinhos:
                    // desenho mais grosso quebra o ritmo da coluna inteira, e o olho le como
                    // erro em vez de enfase.
                    //
                    // O FUNDO DO ITEM ATIVO E `marca-suave`, E ISSO FOI MEDIDO NA TELA DELE.
                    //
                    // O CAMINHO ATE AQUI VALE REGISTRAR, porque foi um erro de leitura meu:
                    // em 06/09/2026 ele mandou a captura do menu dizendo *"assim fica melhor,
                    // tem que mudar o chassi"*, e eu li aquilo como pedido de fundo CHEIO.
                    // Troquei para `bg-marca`, e ele corrigiu: *"o azul em destaque do menu
                    // era pra ficar o mais claro, como estava no menu lateral (azul bem mais
                    // discreto)"*. A captura que ele aprovou JA ERA o `marca-suave`; o que
                    // ele queria era manter, e nao intensificar.
                    //
                    // A LICAO E SOBRE MEDIR, e nao sobre gosto: eu podia ter lido a cor da
                    // captura antes de trocar, e a cor estava la.
                    //
                    // A BARRA LATERAL FICOU, e ela e o ganho que sobrou desta rodada: cor de
                    // fundo sozinha nao chega a quem nao a distingue, e a barra da FORMA ao
                    // estado ativo. `before:` desenha sem um elemento a mais no DOM.
                    'relative',
                    ativo &&
                      'before:absolute before:left-0 before:top-1/2 before:h-5 before:w-[3px] before:-translate-y-1/2 before:rounded-cx-full before:bg-marca',
                    item.destaque && !ativo
                      ? 'bg-marca-suave/60 font-semibold text-marca'
                      : ativo
                        ? 'bg-marca-suave font-semibold text-marca'
                        : 'text-texto-suave hover:bg-superficie-afundada',
                  ].join(' ')}
                >
                  <IconeDoItem nome={item.icone} />
                  {colapsado ? (
                    <span className="cx-so-leitor">{t(item.label)}</span>
                  ) : (
                    <span className="truncate">{t(item.label)}</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>

        {/* ==================================================================
            O RODAPE: CONFIGURACOES E A CONTA.

            Pedido dele em 30/08/2026: "TB CONFIGURACOES E USUARIO VAO PRA BAIXO NO MENU -
            NADA COLORIDO, TUDO NO PADRAO DE CORES DO SISTEMA." Ate entao os dois moravam na
            direita da topbar. O rodape do menu e onde toda plataforma grande os coloca, e e
            o lugar que sobra livre depois que o controle de recolher subiu para o topo.

            NADA COLORIDO, ao pe da letra: o avatar da imagem que ele mandou era um circulo
            ROXO, e roxo nao existe na paleta desta casa. O circulo usa `superficie-afundada`
            com `borda`, que sao os mesmos tokens de qualquer campo do sistema, e acompanham
            tema claro e escuro sem uma segunda decisao.

            A UNICA cor que fica e a do SAIR, e ela nao e enfeite: `erro` e o token que este
            sistema usa para acao destrutiva, e tirar a cor dele deixaria o Sair com o mesmo
            peso visual de "Preferencias". Ele pediu o padrao de cores DO SISTEMA, e este e
            o padrao do sistema.
            ================================================================== */}
        <div className="shrink-0 border-t border-borda p-cx-2">
          {/* O SAIR, VISIVEL, e nao a um clique de distancia.
              MOVIDO PARA CA EM 30/08/2026, por decisao dele em enquete, depois de a medicao
              mostrar que ele morava dentro do menu que o avatar abria. Aquilo contrariava a
              propria regra que o comentario dali citava: ela nao pede so "esta no menu",
              pede visivel SEM precisar abrir outro submenu. A diferenca importa para quem
              tem pressa de sair, que e sempre o caso de quem procura este botao.
              ACIMA DAS OPCOES DO USUARIO, e nao no fim: e o que a regra diz, ao pe da letra.
              NO COLAPSADO fica so o icone, e o nome vem do `aria-label`: em 4rem de largura
              a palavra nao cabe, e rotulo cortado le pior que icone nenhum.
              A COR `erro` FICA, porque e o token que este sistema usa para acao destrutiva,
              e sem ela o Sair teria o mesmo peso visual de "Configuracoes". */}
          {/* COM `sairNaConta` O SAIR MORA DENTRO DO PAINEL DA CONTA, logo abaixo, e nao aqui.
              Pedido do fundador em 12/09/2026. Sem a opcao, o Sair fica onde sempre esteve. */}
          {!sairNaConta && (
            <button
              type="button"
              onClick={aoSair}
              aria-label={t('Sair')}
              title={t('Sair')}
              className={[
                'flex min-h-toque w-full items-center rounded-cx-md text-cx-sm font-medium text-erro hover:bg-erro-suave',
                colapsado && !cabecalhoRecolhe ? 'justify-center' : 'gap-cx-2 px-cx-2',
              ].join(' ')}
            >
              <IconeSair className="h-5 w-5 shrink-0" />
              {/* O ROTULO ACOMPANHA SEMPRE, e e o mesmo padrao que os itens do menu ja usam
                  logo acima: colapsado ele sai da TELA e nao do LEITOR. A primeira versao
                  deste botao removia o texto do DOM, e a medicao no tablet pegou: o `Painel`
                  anunciava o nome e o `Sair` anunciava so "botao". Icone de porta sozinho e
                  adivinhacao, e a regra dele pede a palavra escrita. */}
              {colapsado ? (
                <span className="cx-so-leitor">{t('Sair')}</span>
              ) : (
                <span>{t('Sair')}</span>
              )}
            </button>
          )}

          {/* AJUSTES. So existe quando ha o que ajustar: botao que abre um menu vazio e
              affordance mentirosa, o mesmo defeito que este arquivo ja nomeia na marca. Os
              controles de idioma e tema entram aqui SO no menu colapsado, porque na topbar
              eles continuam sendo o lugar oficial deles pela regra dura do CLAUDE geral. */}
          {opcoesUsuario.length > 0 && (
            // A marca da fronteira: o efeito la em cima usa `closest` nela para saber o
            // que e DENTRO do menu. Sem ela, o proprio clique que abre o painel seria
            // lido como "fora" e o fecharia no mesmo gesto.
            <div className="relative" data-cx-menu-rodape>
              <button
                type="button"
                onClick={() => setMenuAberto((v) => (v === 'ajustes' ? 'nenhum' : 'ajustes'))}
                aria-expanded={menuAberto === 'ajustes'}
                aria-haspopup="menu"
                aria-label={t('Configuracoes')}
                title={t('Configuracoes')}
                className={[
                  'flex min-h-toque w-full items-center rounded-cx-md text-cx-sm text-texto-suave hover:bg-superficie-afundada hover:text-texto',
                  colapsado && !cabecalhoRecolhe ? 'justify-center' : 'gap-cx-2 px-cx-2',
                ].join(' ')}
              >
                <IconeAjustes className="h-5 w-5 shrink-0" />
                {/* O ROTULO ACOMPANHA SEMPRE, colapsado ou nao: ele sai da TELA e nao do
                    LEITOR. Corrigido em 30/08/2026, junto do Sair e pelo mesmo motivo: a
                    medicao no tablet (nav de 64px) mostrou o `Painel` anunciando o nome
                    enquanto estes dois anunciavam so "botao". O `aria-label` ja existia, mas
                    ele NAO substitui o texto para quem le a arvore de acessibilidade item a
                    item, e deixar os dois divergindo era a inconsistencia. */}
                {colapsado ? (
                  <span className="cx-so-leitor">{t('Configuracoes')}</span>
                ) : (
                  <span className="truncate">{t('Configuracoes')}</span>
                )}
              </button>

              {menuAberto === 'ajustes' && (
                <div
                  role="menu"
                  className="cx-cartao absolute bottom-full left-0 z-painel mb-cx-1 w-56 overflow-hidden p-cx-1"
                >
                  {opcoesUsuario.map((o) => (
                    <button
                      key={o.label}
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setMenuAberto('nenhum');
                        o.aoClicar();
                      }}
                      className="flex min-h-toque w-full items-center rounded-cx-md px-cx-3 text-left text-cx-sm text-texto-suave hover:bg-superficie-afundada"
                    >
                      {t(o.label)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* A CONTA, agora so IDENTIDADE. Ela era um botao que abria um menu cujo unico
              item era o Sair; com o Sair visivel acima, aquele menu ficaria vazio, e este
              arquivo ja nomeia esse defeito duas vezes: botao que abre menu vazio, e marca
              que parece botao e nao navega, sao a mesma affordance mentirosa.
              O `title` FICA, porque no colapsado o nome nao cabe e o passar do mouse e o
              unico jeito de ler quem esta logado. */}
          {sairNaConta ? (
            // A CONTA ABRE O PAINEL DA PESSOA, com os dados que o produto passa e o Sair dentro.
            // Pedido do fundador em 12/09/2026: "o Sair sai do menu e vai para dentro quando
            // clicar em conta da medica (tb ali trazer os dados da profissional) Nome completo
            // e CRM". Nao e o menu vazio que o comentario acima nomeia: ha o que mostrar, e o
            // Sair e o gesto que mais importa dentro dele.
            // A marca da fronteira: o efeito la em cima usa `closest` nela para saber o
            // que e DENTRO do menu. Sem ela, o proprio clique que abre o painel seria
            // lido como "fora" e o fecharia no mesmo gesto.
            <div className="relative" data-cx-menu-rodape>
              <button
                type="button"
                data-conta="true"
                onClick={() => setMenuAberto((v) => (v === 'conta' ? 'nenhum' : 'conta'))}
                aria-expanded={menuAberto === 'conta'}
                aria-controls="cx-painel-da-conta"
                title={nomeUsuario}
                className={[
                  'flex min-h-toque w-full items-center rounded-cx-md text-left text-cx-sm text-texto hover:bg-superficie-afundada',
                  colapsado && !cabecalhoRecolhe ? 'justify-center' : 'gap-cx-2 px-cx-2',
                ].join(' ')}
              >
                <span
                  aria-hidden="true"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-cx-full border border-borda bg-superficie-afundada text-cx-xs font-semibold text-texto"
                >
                  {inicial}
                </span>
                {/* NA TELA O NOME CURTO ("Antonio Carlos"); para o leitor de tela, o nome inteiro,
                    porque ali nao ha falta de espaco. */}
                {colapsado ? (
                  <span className="cx-so-leitor">{nomeUsuario}</span>
                ) : (
                  <span className="min-w-0 truncate">{exibicao.curto}</span>
                )}
              </button>

              {menuAberto === 'conta' && (
                // ACIMA DA CONTA, como o menu de ajustes: o pe do menu e a borda de baixo da tela,
                // e abrir para baixo desenharia o painel fora dela.
                <div
                  id="cx-painel-da-conta"
                  className="cx-cartao absolute bottom-full left-0 z-painel mb-cx-1 flex w-64 flex-col gap-cx-2 p-cx-3"
                >
                  {dadosDaConta.length > 0 && (
                    <dl className="flex flex-col gap-cx-2">
                      {dadosDaConta.map((d) => (
                        <div key={d.rotulo} className="flex min-w-0 flex-col">
                          <dt className="text-cx-xs text-texto-suave">{t(d.rotulo)}</dt>
                          {/* O VALOR NAO PASSA POR `t()`: e nome e registro da pessoa. Nulo, a
                              ausencia e dita com todas as letras, e nao preenchida. */}
                          <dd
                            className={
                              d.valor
                                ? 'break-words text-cx-sm font-medium text-texto'
                                : 'text-cx-sm text-texto-suave'
                            }
                          >
                            {d.valor ?? t('Não cadastrado')}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  )}
                  {dadosDaConta.length > 0 && <hr role="separator" className="border-borda" />}
                  <button
                    type="button"
                    onClick={() => {
                      setMenuAberto('nenhum');
                      aoSair();
                    }}
                    className="flex min-h-toque w-full items-center gap-cx-2 rounded-cx-md px-cx-2 text-cx-sm font-medium text-erro hover:bg-erro-suave"
                  >
                    <IconeSair className="h-5 w-5 shrink-0" />
                    <span>{t('Sair')}</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div
              title={nomeUsuario}
              // ATRIBUTO DE DADO, e nao classe de estilo: e a mesma decisao que o `data-destaque`
              // dos itens do menu ja tinha tomado neste arquivo, e pelo mesmo motivo. A conta
              // deixou de ser <button> em 30/08/2026, entao a suite perdeu o seletor que usava
              // (`button[aria-haspopup="menu"]`), e trocar por uma classe de Tailwind a
              // derrubaria de novo no dia em que alguem mexesse no espacamento.
              data-conta="true"
              className={[
                'flex min-h-toque w-full items-center rounded-cx-md text-cx-sm text-texto',
                colapsado && !cabecalhoRecolhe ? 'justify-center' : 'gap-cx-2 px-cx-2',
              ].join(' ')}
            >
              <span
                aria-hidden="true"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-cx-full border border-borda bg-superficie-afundada text-cx-xs font-semibold text-texto"
              >
                {inicial}
              </span>
              {/* O NOME ACOMPANHA SEMPRE, e aqui o buraco era maior que nos botoes: a
                  conta deixou de ser botao em 30/08/2026, entao ela nao tem `aria-label`, e
                  a inicial ao lado esta em `aria-hidden`. Colapsada, quem usa leitor de tela
                  nao tinha NENHUMA forma de saber quem esta logado. */}
              {colapsado ? (
                <span className="cx-so-leitor">{nomeUsuario}</span>
              ) : (
                <span className="min-w-0 truncate">{nomeUsuario}</span>
              )}
            </div>
          )}

          {/* A MARCA DA PLATAFORMA, e nao a do produto.
              Somada em 07/09/2026 por decisao dele em enquete, depois do pedido "TB A LOGO,
              CLICAVEL COM LINK PARA COMPLEXITYDS.COM.BR" e "TEM QUE ESTAR LA TB". Medido
              naquele momento: a marca existia SO na tela de login, e quem ja tinha entrado
              nunca mais via de quem era a plataforma.

              ELA VEM POR ULTIMO, abaixo da conta, e a ordem e a mensagem: primeiro o
              produto, depois quem esta usando, e so entao quem fez. Invertida, a assinatura
              competiria com o nome do produto que o rail acabou de anunciar no topo.

              ABRE EM OUTRA ABA, e `rel="noopener"` nao e formalidade: sem ele a pagina
              aberta ganha referencia a esta pela `window.opener`. Quem esta DENTRO do
              sistema esta trabalhando, e trocar a aba por um site institucional perderia o
              que a pessoa estava fazendo.

              COLAPSADA, so o simbolo fica, e o nome desce para o leitor de tela. O caminho
              contrario, esconder tudo, repetiria o defeito de 30/08/2026 registrado logo
              acima: um alvo clicavel sem rotulo nenhum, mudo para quem nao ve o desenho. */}
          {/* A PECA MORA EM @complexity/brand desde 12/09/2026, e o desenho que ela produz aqui e o
              mesmo de antes. `assinaturaNoMenu` falso tira a assinatura do menu: o Medicina a
              levou para a tela Sobre, por pedido do fundador. */}
          {assinaturaNoMenu && (
            <AssinaturaDaPlataforma
              testId="marca-plataforma-rail"
              className={[
                'mt-cx-2 flex min-h-toque items-center rounded-cx-sm text-cx-xs font-semibold text-texto-suave',
                'hover:text-texto focus-visible:outline focus-visible:outline-2',
                'focus-visible:outline-offset-2 focus-visible:outline-marca',
                colapsado && !cabecalhoRecolhe ? 'justify-center' : 'gap-cx-2 px-cx-2',
              ].join(' ')}
              classeDoSimbolo="h-cx-4 w-cx-4 shrink-0 text-marca"
              nomeSoParaLeitor={colapsado}
            />
          )}
        </div>
      </nav>
    </>
  );
}
